import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Typography from '@mui/material/Typography'
import ErrorBanner from '../shared/ErrorBanner'
import InlineLoading from '../shared/InlineLoading'
import { relativeTime } from '../../helpers/assistant'
import { BOT, RADIUS, SURFACE, TEXT } from '../../theme/tokens'

/**
 * The person's past conversations, newest activity first: each opens on a
 * click; Load more reads the next 20.
 */
const HistoryView = ({ history, currentId, onLoad, onOpen }) => {
    useEffect(() => {
        onLoad()
    }, [])

    return (
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {history.items.length === 0 && history.loading && (
                <InlineLoading label="Loading your conversations…" py={4} />
            )}
            {history.error && (
                <>
                    <ErrorBanner message={history.error} />
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onLoad()}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        Try again
                    </Button>
                </>
            )}
            {history.loaded &&
                !history.loading &&
                !history.error &&
                history.items.length === 0 && (
                    <Typography
                        sx={{ typography: 'body2', color: BOT.meta, py: 2 }}
                    >
                        No conversations yet. Ask a question to start one.
                    </Typography>
                )}
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {history.items.map((conversation) => (
                    <Box component="li" key={conversation.id} sx={{ mb: 0.5 }}>
                        <ButtonBase
                            onClick={() => onOpen(conversation.id)}
                            aria-current={
                                conversation.id === currentId
                                    ? 'true'
                                    : undefined
                            }
                            sx={{
                                width: '100%',
                                display: 'block',
                                textAlign: 'left',
                                px: 1.25,
                                py: 1,
                                borderRadius: `${RADIUS.sm}px`,
                                backgroundColor:
                                    conversation.id === currentId
                                        ? SURFACE.hover
                                        : 'transparent',
                                '&:hover, &.Mui-focusVisible': {
                                    backgroundColor: SURFACE.hover,
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    typography: 'body2',
                                    color: TEXT.primary,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {conversation.title || 'Untitled conversation'}
                            </Typography>
                            <Typography
                                sx={{ typography: 'caption', color: BOT.meta }}
                            >
                                {relativeTime(conversation.updated_on)}
                            </Typography>
                        </ButtonBase>
                    </Box>
                ))}
            </Box>
            {history.nextCursor && (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onLoad(history.nextCursor)}
                    disabled={history.loading}
                    sx={{ alignSelf: 'center' }}
                >
                    {history.loading ? 'Loading…' : 'Load more'}
                </Button>
            )}
        </Box>
    )
}

HistoryView.propTypes = {
    history: PropTypes.shape({
        items: PropTypes.array.isRequired,
        nextCursor: PropTypes.string,
        loading: PropTypes.bool,
        loaded: PropTypes.bool,
        error: PropTypes.string,
    }).isRequired,
    currentId: PropTypes.string,
    onLoad: PropTypes.func.isRequired,
    onOpen: PropTypes.func.isRequired,
}

export default HistoryView
